/**
 * ErrorBoundary — captura erros de renderização React e exibe uma tela de erro
 * amigável em vez de uma tela branca.
 */

import { Component, type ReactNode } from 'react';
import { LdsTypography, LdsButton, LdsAlert } from '@lds/react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: '16px',
            padding: '32px',
          }}
        >
          <LdsAlert
            color="critical"
            description="Ocorreu um erro inesperado na aplicação."
          />
          <LdsTypography variant="headingMd">
            Algo deu errado
          </LdsTypography>
          <LdsTypography variant="bodySm">
            {this.state.error?.message ?? 'Erro desconhecido'}
          </LdsTypography>
          <LdsButton variant="contained" onClick={this.handleRetry}>
            Tentar novamente
          </LdsButton>
        </div>
      );
    }

    return this.props.children;
  }
}
